import { Toast as ArkToast, createToaster, Toaster } from "@ark-ui/react/toast"
import { CheckCircle, Info, Warning, WarningCircle, X } from "@phosphor-icons/react"
import type { ReactNode } from "react"

import { cn } from "@/lib/utils/cn"

type Intent = "info" | "success" | "warning" | "error"

/**
 * Global toaster singleton. Ark UI'ın createToaster pattern'ı:
 *   toaster.create({ title, description, type: "success" })
 * Görsel stil bizim tarafımızda (Toaster render fn).
 */
export const toaster = createToaster({
  placement: "top-end",
  overlap: true,
  gap: 12,
  max: 5,
})

const iconFor: Record<Intent, ReactNode> = {
  info: <Info size={18} weight="fill" className="text-ok-info" />,
  success: <CheckCircle size={18} weight="fill" className="text-ok-success" />,
  warning: <Warning size={18} weight="fill" className="text-ok-warning" />,
  error: <WarningCircle size={18} weight="fill" className="text-ok-critical" />,
}

function intentFromType(type: string | undefined): Intent {
  if (type === "success" || type === "warning" || type === "error") {
    return type
  }
  return "info"
}

export function ToastViewport() {
  return (
    <Toaster toaster={toaster}>
      {(toast) => {
        const intent = intentFromType(toast.type)
        return (
          <ArkToast.Root
            key={toast.id}
            className={cn(
              "pointer-events-auto w-80 rounded-ok border border-ok-border bg-ok-bg shadow-ok-lg",
              "flex items-start gap-3 p-3",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
            )}
          >
            <div className="mt-0.5 shrink-0">{iconFor[intent]}</div>
            <div className="flex-1 min-w-0">
              <ArkToast.Title className="text-sm font-medium text-ok-foreground">
                {toast.title}
              </ArkToast.Title>
              {toast.description ? (
                <ArkToast.Description className="mt-0.5 text-xs text-ok-foreground-muted">
                  {toast.description}
                </ArkToast.Description>
              ) : null}
              {toast.action ? (
                <ArkToast.ActionTrigger className="mt-2 inline-flex h-7 items-center rounded-ok bg-ok-primary px-2.5 text-xs font-medium text-ok-primary-foreground hover:bg-ok-primary-hover">
                  {toast.action.label}
                </ArkToast.ActionTrigger>
              ) : null}
            </div>
            <ArkToast.CloseTrigger
              aria-label="Close"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-ok text-ok-foreground-muted hover:bg-ok-bg-muted"
            >
              <X size={12} weight="bold" />
            </ArkToast.CloseTrigger>
          </ArkToast.Root>
        )
      }}
    </Toaster>
  )
}

export const toast = {
  info: (title: string, description?: string) =>
    toaster.create({ title, description, type: "info" }),
  success: (title: string, description?: string) =>
    toaster.create({ title, description, type: "success" }),
  warning: (title: string, description?: string) =>
    toaster.create({ title, description, type: "warning" }),
  error: (title: string, description?: string) =>
    toaster.create({ title, description, type: "error" }),
}
